import { redirect } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'

import { loadMe } from './me.ts'
import { hasAnyRole, isProblemError, rolesOf, type Me, type Role } from '@volee/api-client'
import { registrationStatusQueryOptions } from '../data/registration.ts'
import { familyEnrolmentStatusQueryOptions } from '../data/family-enrolment.ts'
import { NAV_PATHS } from '../navigation/paths.ts'

// The per-role route guard. UX and not security, and why it runs in `beforeLoad`:
// apps/frontend/docs/razionale-che-il-codice-indica.md.

export interface GuardRequirements {
  readonly queryClient: QueryClient
  /** The roles allowed through. Empty = being authenticated is enough. */
  readonly requiredRoles?: readonly Role[]
  /** Where to return after signing in. */
  readonly currentPath: string
}

/** Throws a redirect when the user cannot be on this route: no session → `/sign-in`,
 *  carrying `returnTo` and the `required` reason; a session without the role → `/forbidden`, which is not an
 *  invitation to authenticate again. */
export async function guardRoute({
  queryClient,
  requiredRoles = [],
  currentPath,
}: GuardRequirements): Promise<void> {
  const me = await loadMe(queryClient)

  if (me === null) {
    // `NAV_PATHS` and not the route objects: the routes import this guard, so the other
    // direction would be an import cycle.
    throw redirect({
      to: NAV_PATHS.signIn,
      search: { returnTo: currentPath, reason: 'required' },
    })
  }

  if (mustReadTheAutonomyNotice(me)) {
    throw redirect({ to: NAV_PATHS.autonomy, search: { returnTo: currentPath } })
  }

  if (!hasAnyRole(rolesOf(me), requiredRoles)) {
    throw redirect({ to: NAV_PATHS.forbidden })
  }
}

/** «Da oggi decidi tu» stands before every guarded page, and without a mail of the player's own
 *  until they add one: from 14 the password is theirs. `!= null` and not `!== null`: an API
 *  older than the bundle leaves the field out, and `undefined` would send every route here. */
export function mustReadTheAutonomyNotice(me: Me): boolean {
  return me.autonomy != null
}

export interface PendingRegistrationRequirements {
  readonly queryClient: QueryClient
  /** Where to return after signing in, if the caller has no request to see. */
  readonly currentPath: string
}

/** Asks for the request itself, not for a session or a token: why neither works here is in
 *  the design doc (docs/superpowers/specs/2026-09-02-registrazione-dirigente-design.md). */
export async function guardPendingRegistration({
  queryClient,
  currentPath,
}: PendingRegistrationRequirements): Promise<void> {
  try {
    await queryClient.ensureQueryData(registrationStatusQueryOptions)
  } catch (error: unknown) {
    if (isProblemError(error) && error.status === 404) {
      throw redirect({ to: NAV_PATHS.signIn, search: { returnTo: currentPath } })
    }
  }
}

/** The family twin of `guardPendingRegistration`: asks for the request itself, and sends a
 *  holder with none back to the login. A 404 is the normal "never asked" case, not an error. */
export async function guardPendingEnrolment({
  queryClient,
  currentPath,
}: PendingRegistrationRequirements): Promise<void> {
  try {
    await queryClient.ensureQueryData(familyEnrolmentStatusQueryOptions)
  } catch (error: unknown) {
    if (isProblemError(error) && error.status === 404) {
      throw redirect({ to: NAV_PATHS.signIn, search: { returnTo: currentPath } })
    }
  }
}
